import { useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateSeller } from "@/hooks/use-sellers";
import { describeCallableError } from "@/lib/callables";
import { normalizeCnpj } from "@/lib/format";
import type { Seller } from "@/types";

/**
 * Correção do cadastro do estabelecimento.
 *
 * Só os campos que um administrador corrige de verdade: nome, contato,
 * documento e endereço. Horários, entrega e catálogo continuam sendo do
 * vendedor, no aplicativo dele. Trazer tudo para cá transformaria o painel numa
 * segunda interface de cadastro, e as duas divergiriam.
 */
export function SellerEditDialog({
  seller,
  open,
  onOpenChange,
}: {
  seller: Seller;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const update = useUpdateSeller();
  const [form, setForm] = useState(() => ({
    name: seller.name ?? "",
    email: seller.email ?? "",
    phone: seller.phone ?? "",
    cnpj: seller.cnpj ?? "",
    description: seller.description ?? "",
    street: seller.address?.street ?? "",
    number: seller.address?.number ?? "",
    neighborhood: seller.address?.neighborhood ?? "",
    city: seller.address?.city ?? "",
    state: seller.address?.state ?? "",
  }));

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      await update.mutateAsync({
        sellerId: seller.id,
        data: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          cnpj: normalizeCnpj(form.cnpj),
          description: form.description.trim(),
          address: {
            ...seller.address,
            street: form.street.trim(),
            number: form.number.trim(),
            neighborhood: form.neighborhood.trim(),
            city: form.city.trim(),
            state: form.state.trim().toUpperCase(),
          },
        },
      });
      toast.success("Cadastro atualizado.");
      onOpenChange(false);
    } catch (error) {
      toast.error(describeCallableError(error));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Corrigir cadastro</DialogTitle>
          <DialogDescription>
            O vendedor vê estas mudanças no aplicativo dele. O CNPJ precisa ter 14 caracteres
            (só números ou o novo formato com letras) para o faturamento emitir Pix e boleto.
          </DialogDescription>
        </DialogHeader>

        <form id="seller-edit" onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="s-name">Nome</Label>
            <Input id="s-name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="s-email">E-mail</Label>
              <Input id="s-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-phone">Telefone</Label>
              <Input id="s-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-cnpj">CNPJ</Label>
            {/* O CNPJ alfanumérico tem letras: teclado de texto, não numérico. */}
            <Input
              id="s-cnpj"
              value={form.cnpj}
              onChange={(e) => set("cnpj", e.target.value.toUpperCase())}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              maxLength={18}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="s-desc">Descrição</Label>
            <Textarea id="s-desc" value={form.description} onChange={(e) => set("description", e.target.value)} />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_6rem]">
            <div className="space-y-1.5">
              <Label htmlFor="s-street">Rua</Label>
              <Input id="s-street" value={form.street} onChange={(e) => set("street", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-number">Número</Label>
              <Input id="s-number" value={form.number} onChange={(e) => set("number", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_1fr_5rem]">
            <div className="space-y-1.5">
              <Label htmlFor="s-neigh">Bairro</Label>
              <Input id="s-neigh" value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-city">Cidade</Label>
              <Input id="s-city" value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-state">UF</Label>
              <Input id="s-state" value={form.state} maxLength={2} onChange={(e) => set("state", e.target.value)} />
            </div>
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="submit" form="seller-edit" disabled={update.isPending}>
            {update.isPending ? <Loader2 className="animate-spin" /> : null}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
